// Shared imports
const nameUtils = require('shared/name-utils')
// Client lib imports
const airbrake = require('client/lib/airbrake')
const { API: ArticleAPI } = require('client/models/article')
// Client V2 common imports
const tag = require('client-v2/common/utils/hyperscript')
const toastsActions = require('client-v2/common/containers/toasts/actions')
const articleModelActions = require('client-v2/common/models/article/actions')
const { collectionProfileHref } = require('client-v2/common/models/collection/actions')
const extension = require('client-v2/common/utils/extension')
// Sibling imports
const API = require('./api')
const selectors = require('./selectors')

// Action types

const SHOW = 'COLLECTION_SELECT_MODAL_SHOW'
const HIDE = 'COLLECTION_SELECT_MODAL_HIDE'
const ARTICLE_FAILED = 'COLLECTION_SELECT_MODAL_ARTICLE_FAILED'
const ARTICLE_LOADED = 'COLLECTION_SELECT_MODAL_ARTICLE_LOADED'
const ARTICLE_LOADING = 'COLLECTION_SELECT_MODAL_ARTICLE_LOADING'
const COLLECTIONS_FAILED = 'COLLECTION_SELECT_MODAL_COLLECTIONS_FAILED'
const COLLECTIONS_LOADED = 'COLLECTION_SELECT_MODAL_COLLECTIONS_LOADED'
const COLLECTIONS_LOADING = 'COLLECTION_SELECT_MODAL_COLLECTIONS_LOADING'
const COLLECTION_CREATED = 'COLLECTION_SELECT_MODAL_COLLECTION_CREATED'
const COLLECTION_UPDATED = 'COLLECTION_SELECT_MODAL_COLLECTION_UPDATED'
const COMMENT_FAILED = 'COLLECTION_SELECT_MODAL_COMMENT_FAILED'
const COMMENT_LOADED = 'COLLECTION_SELECT_MODAL_COMMENT_LOADED'
const COMMENT_LOADING = 'COLLECTION_SELECT_MODAL_COMMENT_LOADING'
const CREATE_FAILED = 'COLLECTION_SELECT_MODAL_CREATE_FAILED'
const CREATE_LOADED = 'COLLECTION_SELECT_MODAL_CREATE_LOADED'
const CREATE_LOADING = 'COLLECTION_SELECT_MODAL_CREATE_LOADING'
const SET_COMMENT_TEXT = 'COLLECTION_SELECT_MODAL_SET_COMMENT_TEXT'
const SET_FILTER_TEXT = 'COLLECTION_SELECT_MODAL_SET_FILTER_TEXT'
const UNMIX_FAILED = 'COLLECTION_SELECT_MODAL_UNMIX_FAILED'
const UNMIX_LOADED = 'COLLECTION_SELECT_MODAL_UNMIX_LOADED'
const UNMIX_LOADING = 'COLLECTION_SELECT_MODAL_UNMIX_LOADING'
const UNMIX_PROMPT_TOGGLE = 'COLLECTION_SELECT_MODAL_UNMIX_PROMPT_TOGGLE'

// Action enums

const COLLECTION_UPDATE_PENDING = 'COLLECTION_SELECT_MODAL_COLLECTION_UPDATE_PENDING'

// Action creators

function hide() {
  return {
    type: HIDE
  }
}

function setCommentText(commentText) {
  return {
    type: SET_COMMENT_TEXT,
    payload: nameUtils.cleanName(commentText)
  }
}

function setFilterText(filterText) {
  return {
    type: SET_FILTER_TEXT,
    payload: filterText
  }
}

function collectionCreated(collection) {
  return {
    type: COLLECTION_CREATED,
    payload: collection
  }
}

function collectionUpdated(collection) {
  return {
    type: COLLECTION_UPDATED,
    payload: collection
  }
}

function toggleUnmixPrompt(bool) {
  return {
    type: UNMIX_PROMPT_TOGGLE,
    payload: bool
  }
}

function showUnmixPrompt() {
  return toggleUnmixPrompt(true)
}

// Action dispatchers

function show({ article, url, anchorId }) {
  return async function (dispatch, getState) {
    const state = getState()
    const showUrlId = article && article['url_id']
    const modalUrlId = selectors.urlId(state)
    if (modalUrlId && showUrlId === modalUrlId) {
      return dispatch(hide())
    }
    dispatch({
      type: SHOW,
      payload: { article, url, anchorId }
    })
    await dispatch(fetch())
  }
}

function fetch() {
  return async function (dispatch, getState) {
    if (!selectors.isArticleLoaded(getState())) {
      await dispatch(fetchArticle())
    }
    if (!selectors.isArticleFailed(getState())) {
      await dispatch(fetchCollections())
    }
  }
}

function fetchArticle() {
  return async function (dispatch, getState) {
    const url = selectors.url(getState())
    dispatch({
      type: ARTICLE_LOADING
    })
    try {
      dispatch({
        type: ARTICLE_LOADED,
        payload: await API.extractArticleFromUrl(url)
      })
    } catch (err) {
      airbrake.notify(err)
      dispatch({
        type: ARTICLE_FAILED
      })
    }
  }
}

function fetchCollections() {
  return async function (dispatch, getState) {
    const urlId = selectors.urlId(getState())
    dispatch({
      type: COLLECTIONS_LOADING
    })
    try {
      dispatch({
        type: COLLECTIONS_LOADED,
        payload: await API.fetchCollections(urlId)
      })
    } catch (err) {
      airbrake.notify(err)
      dispatch({
        type: COLLECTIONS_FAILED
      })
    }
  }
}

function submitComment() {
  return async function (dispatch, getState) {
    const state = getState()
    const text = selectors.commentText(state)
    if (!text) {
      return // Nothing to submit
    }
    if (selectors.isCommentPending(state)) {
      return // Submission is already in-progress
    }
    dispatch({
      type: COMMENT_LOADING
    })
    try {
      const urlId = selectors.urlId(state)
      await ArticleAPI.addComment(urlId, text)
      dispatch({
        type: COMMENT_LOADED
      })
      const article = selectors.article(state)
      dispatch(showCommentedOnArticleToast(article))
    } catch (err) {
      airbrake.notify(err)
      dispatch({
        type: COMMENT_FAILED
      })
    }
  }
}

function cancelUnmix() {
  return async function (dispatch, getState) {
    const state = getState()
    const urlId = selectors.urlId(state)
    const isMixed = selectors.isMixed(state)
    if (isMixed) {
      return dispatch(toggleUnmixPrompt(false))
    }
    dispatch({
      type: UNMIX_LOADING
    })
    try {
      await API.addToMixedCollection(urlId)
      dispatch({
        type: UNMIX_LOADED
      })
      dispatch(articleModelActions.articleMixesUpdated(urlId, ['mixed']))
      extension.clearUrlCache(urlId)
      dispatch(toggleUnmixPrompt(false))
    } catch (err) {
      airbrake.notify(err)
      dispatch({
        type: UNMIX_FAILED
      })
    }
  }
}

function confirmUnmix() {
  return async function (dispatch, getState) {
    const state = getState()
    const urlId = selectors.urlId(state)
    const isMixed = selectors.isMixed(state)
    if (!isMixed) {
      return dispatch(hide())
    }
    dispatch({
      type: UNMIX_LOADING
    })
    try {
      await API.removeFromMixedCollection(urlId)
      dispatch(articleModelActions.articleMixesUpdated(urlId, []))
      extension.clearUrlCache(urlId)
      dispatch({
        type: UNMIX_LOADED
      })
      dispatch(hide())
      dispatch(toastsActions.showNotice({
        message: 'Removed from your profile'
      }))
    } catch (err) {
      airbrake.notify(err)
      dispatch({
        type: UNMIX_FAILED
      })
    }
  }
}

function toggleCollection({ collection }) {
  return async function (dispatch, getState) {
    if (collection.isMixed === COLLECTION_UPDATE_PENDING) {
      return // This action is already in-progress
    }
    const wasMixed = selectors.isMixed(getState())
    dispatch(collectionUpdated({
      slug: collection.slug,
      isMixed: COLLECTION_UPDATE_PENDING
    }))
    try {
      if (!collection.id) {
        // "Suggested" collection does not actually exist; create it
        const result = await API.createCollection(collection)
        dispatch(collectionCreated(result))
        return dispatch(toggleCollection({ collection: result }))
      }
      if (collection.isMixed) {
        await dispatch(removeFromCollection(collection))
      } else {
        await dispatch(addToCollection(collection))
      }
      await dispatch(updateMixedCollection(wasMixed))
    } catch (err) {
      airbrake.notify(err)
      // Revert the isMixed state to its previous value
      dispatch(collectionUpdated({
        slug: collection.slug,
        isMixed: collection.isMixed
      }))
    }
  }
}

function addToCollection(collection) {
  return async function (dispatch, getState) {
    const urlId = selectors.urlId(getState())
    await API.addToCollection(urlId, collection.id)
    dispatch(collectionUpdated({
      slug: collection.slug,
      isMixed: true,
      'modified_at': new Date().toISOString()
    }))
    dispatch(showAddedToCollectionToast(collection))
  }
}

function removeFromCollection(collection) {
  return async function (dispatch, getState) {
    const urlId = selectors.urlId(getState())
    const result = await API.removeFromCollection(urlId, collection.id)
    if (result.mixDeleted) {
      // Turn the collection back into a "Suggested" item
      dispatch(collectionUpdated({
        slug: collection.slug,
        id: 0,
        isMixed: false,
        'modified_at': ''
      }))
      dispatch(showRemovedFromDeletedCollectionToast(collection))
    } else {
      dispatch(collectionUpdated({
        slug: collection.slug,
        isMixed: false,
        'modified_at': new Date().toISOString()
      }))
      dispatch(showRemovedFromCollectionToast(collection))
    }
  }
}

function createCollectionFromFilter() {
  return async function (dispatch, getState) {
    const state = getState()
    if (selectors.isCreatePending(state)) {
      return // Collection creation is already in-progress
    }
    if (!selectors.canCreateFromFilter(state)) {
      return // Nothing to create
    }
    const modal = selectors.containerState(state)
    const slug = selectors.filterSlug(state)
    const collectionIndex = modal.findCollectionIndex({ slug })
    if (collectionIndex !== -1) {
      // Collection already exists; toggle instead of creating
      return dispatch(toggleCollection({
        collection: modal.collections.get(collectionIndex)
      }))
    }
    try {
      const collection = await API.createCollection({
        name: selectors.filterText(state)
      })
      dispatch({
        type: CREATE_LOADED,
        payload: collection
      })
      return dispatch(toggleCollection({ collection }))
    } catch (err) {
      airbrake.notify(err)
      dispatch({
        type: CREATE_FAILED
      })
    }
  }
}

function updateMixedCollection(wasMixed) {
  return async function (dispatch, getState) {
    const state = getState()
    const urlId = selectors.urlId(state)
    const isMixed = selectors.isMixed(state)
    let collectionIds = selectors.curatedCollectionIds(state)
    if (!collectionIds.length) {
      if (wasMixed) {
        collectionIds = [ 'mixed', ...collectionIds ]
      }
      dispatch(articleModelActions.articleMixesUpdated(urlId, collectionIds))
      extension.clearUrlCache(urlId)
      dispatch(toggleUnmixPrompt(true))
    } else {
      if (!wasMixed && isMixed) {
        await API.addToMixedCollection(urlId)
      } else if (wasMixed && !isMixed) {
        await API.removeFromMixedCollection(urlId)
      }
      if (isMixed) {
        collectionIds = [ 'mixed', ...collectionIds ]
      }
      dispatch(articleModelActions.articleMixesUpdated(urlId, collectionIds))
      extension.clearUrlCache(urlId)
    }
  }
}

// Exports

module.exports = {
  // Action types
  SHOW,
  HIDE,
  ARTICLE_FAILED,
  ARTICLE_LOADED,
  ARTICLE_LOADING,
  COLLECTIONS_FAILED,
  COLLECTIONS_LOADED,
  COLLECTIONS_LOADING,
  COLLECTION_CREATED,
  COLLECTION_UPDATED,
  COMMENT_FAILED,
  COMMENT_LOADED,
  COMMENT_LOADING,
  CREATE_FAILED,
  CREATE_LOADED,
  CREATE_LOADING,
  SET_COMMENT_TEXT,
  SET_FILTER_TEXT,
  UNMIX_FAILED,
  UNMIX_LOADED,
  UNMIX_LOADING,
  UNMIX_PROMPT_TOGGLE,
  // Action enums
  COLLECTION_UPDATE_PENDING,
  // Action creators
  cancelUnmix,
  hide,
  setCommentText,
  setFilterText,
  showUnmixPrompt,
  // Action dispatchers
  confirmUnmix,
  createCollectionFromFilter,
  fetch,
  show,
  submitComment,
  toggleCollection
}

// Toast helpers

function showAddedToCollectionToast(collection) {
  return toastsActions.showNotice({
    message: () => tag([
      'Added to your ',
      tag('a', {
        href: collectionProfileHref(collection)
      },
        collection.name
      ),
      ' collection'
    ])
  })
}

function showRemovedFromCollectionToast(collection) {
  return toastsActions.showNotice({
    message: () => tag([
      'Removed from your ',
      tag('a', {
        href: collectionProfileHref(collection)
      },
        collection.name
      ),
      ' collection'
    ])
  })
}

function showRemovedFromDeletedCollectionToast(collection) {
  return toastsActions.showNotice({
    message: () => tag([
      'Removed from your ',
      tag('b', collection.name),
      ' collection'
    ])
  })
}

function showCommentedOnArticleToast(article) {
  // Work-around circular dependency
  const { articleProfileHref } = require('client-v2/common/models/article/actions')
  return toastsActions.showNotice({
    message: () => tag([
      'Comment posted in ',
      tag('a', {
        href: articleProfileHref(article)
      },
        article.title
      )
    ])
  })
}
